/**
 * 🌅 MyPersonalContextCard — Hero "Mon contexte personnel actuel"
 *
 * Architecture editorial premium :
 *  - Hero (archetype Prism + posture clé + tension active)
 *  - 5 sections déroulantes (Profil, Posture, Tension, Modules, Pulse)
 *  - Adaptive : ne montre QUE les blocs présents (pas d'écran vide)
 *
 * Wording 100% donnée structurée — aucune prose générée.
 * Design system : ivoire/or/charbon clair, serif Cormorant pour le hero,
 * Inter pour le body. Pas d'effet dashboard, sensation "dossier vivant".
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Sparkles, Compass, Activity, Layers, Wind, ArrowRight,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const T = {
  ivory: '#FBF9F3',
  card: '#FFFFFF',
  ink: '#1C1147',
  text: '#1F2937',
  muted: '#6B7280',
  border: '#E8E2D2',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  charcoal: '#2A2319',
  ivoryDeep: '#FFFCEC',
};

// Couleurs Prism (archetypes)
const PRISM_COLORS = {
  yellow: { tint: '#FFF4D6', solid: '#D4A93C', label: 'Jaune' },
  green:  { tint: '#E0EFDC', solid: '#5C8A4C', label: 'Vert' },
  red:    { tint: '#F8DBD0', solid: '#A24A33', label: 'Rouge' },
  blue:   { tint: '#DDE6F1', solid: '#3F628A', label: 'Bleu' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

function ExpandableSection({ icon: Icon, title, badge, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: T.border }}
      data-testid={testid}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-5 px-6 hover:bg-[#FAF7EE] transition-colors group"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: T.ivoryDeep, color: T.goldDeep }}
          >
            <Icon size={17} strokeWidth={1.6} />
          </div>
          <div className="text-left">
            <div
              className="text-[15px] font-medium tracking-wide"
              style={{ color: T.charcoal, fontFamily: '"Cormorant Garamond", serif' }}
            >
              {title}
            </div>
            {badge && (
              <div className="text-[11px] uppercase tracking-[0.15em] mt-0.5" style={{ color: T.muted }}>
                {badge}
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          size={18}
          strokeWidth={1.6}
          style={{
            color: T.muted,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-6 pb-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PrismHeroBanner({ prism }) {
  const palette = PRISM_COLORS[prism?.dominant_color] || PRISM_COLORS.yellow;
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: `linear-gradient(135deg, ${T.ivoryDeep} 0%, ${palette.tint} 100%)`,
        border: `1px solid ${T.border}`,
      }}
      data-testid="personal-context-hero"
    >
      {/* Halo discret */}
      <div
        className="absolute -right-32 -top-32 w-72 h-72 rounded-full blur-3xl pointer-events-none"
        style={{ background: palette.solid, opacity: 0.10 }}
      />
      <div className="relative px-7 pt-7 pb-7 md:px-10 md:pt-10 md:pb-9">
        <div className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: T.goldDeep }}>
          Mon dossier personnel
        </div>
        <div
          className="text-3xl md:text-4xl leading-tight mb-2"
          style={{ color: T.ink, fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
        >
          {prism?.archetype_name || '—'}
        </div>
        {prism?.archetype_tagline && (
          <div className="text-[15px] italic leading-relaxed mb-5" style={{ color: T.text, opacity: 0.78 }}>
            « {prism.archetype_tagline} »
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {(prism?.colors_ranked || []).slice(0, 4).map((c) => (
            <span
              key={c.color}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium"
              style={{
                background: PRISM_COLORS[c.color]?.tint || T.ivoryDeep,
                color: PRISM_COLORS[c.color]?.solid || T.charcoal,
                border: `1px solid ${PRISM_COLORS[c.color]?.solid || T.border}30`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRISM_COLORS[c.color]?.solid }} />
              {PRISM_COLORS[c.color]?.label || c.color}
              <span className="opacity-60">{Math.round(c.score)}</span>
            </span>
          ))}
          {prism?.mbti_code && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium border"
              style={{ color: T.charcoal, borderColor: T.border, background: '#FFFFFF' }}
            >
              {prism.mbti_code}
            </span>
          )}
        </div>
        {prism?.computed_at && (
          <div className="text-[10px] mt-5 uppercase tracking-[0.15em]" style={{ color: T.muted }}>
            Profil établi le {fmtDate(prism.computed_at)}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyHeroPrompt({ user }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed text-center py-14 px-6"
      style={{ borderColor: T.border, background: T.ivoryDeep }}
      data-testid="personal-context-hero-empty"
    >
      <Sparkles size={28} className="mx-auto mb-3" strokeWidth={1.4} style={{ color: T.goldDeep }} />
      <div
        className="text-2xl mb-2"
        style={{ color: T.ink, fontFamily: '"Cormorant Garamond", serif' }}
      >
        Activez votre contexte personnel
      </div>
      <div className="text-sm max-w-md mx-auto leading-relaxed mb-5" style={{ color: T.muted }}>
        {user?.name ? user.name + ', dès' : 'Dès'} votre premier profil Prism complété,
        votre dossier personnel s'enrichira automatiquement : archetype, posture, tensions actives, modules en progression.
      </div>
      <a
        href="/discovery"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
        style={{ background: T.charcoal, color: T.ivoryDeep }}
        data-testid="personal-context-cta-prism"
      >
        Démarrer Prism <ArrowRight size={14} />
      </a>
    </div>
  );
}

export default function MyPersonalContextCard() {
  const { token } = useAuth();
  const API = getApiUrl();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`${API}/coaching-context/my-personal-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setError(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API, token]);

  const hasAnyData = useMemo(
    () => data && (data.has_prism || data.has_modules || data.has_pulse),
    [data]
  );

  if (loading) {
    return (
      <div className="rounded-2xl border animate-pulse h-48"
           style={{ borderColor: T.border, background: T.card }}
           data-testid="personal-context-loading" />
    );
  }
  if (error) {
    return (
      <div
        className="rounded-2xl border p-5 text-sm"
        style={{ borderColor: T.border, background: T.card, color: T.muted }}
        data-testid="personal-context-error"
      >
        Impossible de charger votre contexte pour le moment.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="personal-context-card">
      {/* HERO */}
      {data?.has_prism ? <PrismHeroBanner prism={data.prism} /> : <EmptyHeroPrompt user={data?.user} />}

      {/* SECTIONS DÉROULANTES (ordre : profil → posture → tension → modules → pulse) */}
      {hasAnyData && (
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: T.border, background: T.card }}
        >
          {/* 1. Profil dominant (synthèse exécutive) */}
          {data?.has_prism && data.prism?.executive_summary && (
            <ExpandableSection
              icon={Compass}
              title="Profil dominant"
              badge="Vue d'ensemble"
              defaultOpen
              testid="section-profile"
            >
              <p className="text-[14.5px] leading-[1.7]" style={{ color: T.text }}>
                {data.prism.executive_summary}
              </p>
            </ExpandableSection>
          )}

          {/* 2. Posture clé */}
          {data?.posture && (
            <ExpandableSection
              icon={Sparkles}
              title="Posture clé"
              badge="Communication & décision"
              testid="section-posture"
            >
              <div className="space-y-4">
                {data.posture.communication_style && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
                         style={{ color: T.goldDeep }}>Style de communication</div>
                    <div className="text-[14px] leading-relaxed" style={{ color: T.text }}>
                      {data.posture.communication_style}
                    </div>
                  </div>
                )}
                {data.posture.decision_style && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
                         style={{ color: T.goldDeep }}>Style de décision</div>
                    <div className="text-[14px] leading-relaxed" style={{ color: T.text }}>
                      {data.posture.decision_style}
                    </div>
                  </div>
                )}
                {data.posture.signature_move_title && (
                  <div className="pt-2 border-t" style={{ borderColor: T.border }}>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
                         style={{ color: T.goldDeep }}>Mouvement signature</div>
                    <div className="text-[14px] italic" style={{ color: T.charcoal }}>
                      {data.posture.signature_move_title}
                    </div>
                  </div>
                )}
              </div>
            </ExpandableSection>
          )}

          {/* 3. Tension active */}
          {data?.tension && (data.tension.blind_spots?.length || data.tension.stress_profile || data.tension.development_axes?.length) && (
            <ExpandableSection
              icon={Activity}
              title="Tension active"
              badge="Sous pression & angles morts"
              testid="section-tension"
            >
              <div className="space-y-4">
                {data.tension.stress_profile && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
                         style={{ color: T.goldDeep }}>Sous pression</div>
                    <div className="text-[14px] leading-relaxed" style={{ color: T.text }}>
                      {data.tension.stress_profile}
                    </div>
                  </div>
                )}
                {data.tension.blind_spots?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-2"
                         style={{ color: T.goldDeep }}>Angles morts</div>
                    <ul className="space-y-1.5">
                      {data.tension.blind_spots.map((b, i) => (
                        <li key={i} className="text-[14px] leading-relaxed flex gap-2" style={{ color: T.text }}>
                          <span style={{ color: T.gold }}>—</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.tension.development_axes?.length > 0 && (
                  <div className="pt-2 border-t" style={{ borderColor: T.border }}>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-2"
                         style={{ color: T.goldDeep }}>Axes de développement</div>
                    <ul className="space-y-1.5">
                      {data.tension.development_axes.map((d, i) => (
                        <li key={i} className="text-[14px] leading-relaxed flex gap-2" style={{ color: T.text }}>
                          <span style={{ color: T.gold }}>—</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ExpandableSection>
          )}

          {/* 4. Modules en progression */}
          {data?.has_modules && (
            <ExpandableSection
              icon={Layers}
              title="Modules en progression"
              badge={`${data.modules_in_progress?.length || 0} actifs`}
              testid="section-modules"
            >
              <ul className="space-y-3.5">
                {(data.modules_in_progress || []).map((m) => (
                  <li
                    key={m.objective_id}
                    className="flex items-start justify-between gap-4 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.18em] mb-0.5"
                           style={{ color: T.muted }}>
                        Module {m.module_id || '—'}
                      </div>
                      <div className="text-[14px] leading-snug" style={{ color: T.charcoal }}>
                        {m.title}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0 w-[120px]">
                      <div className="text-[12px] tabular-nums font-medium"
                           style={{ color: T.charcoal }}>
                        {m.progress_percentage}%
                      </div>
                      <div className="w-full h-1 rounded-full overflow-hidden"
                           style={{ background: T.border }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${m.progress_percentage}%`,
                            background: `linear-gradient(90deg, ${T.gold}, ${T.goldDeep})`,
                          }}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </ExpandableSection>
          )}

          {/* 5. Pulse récent */}
          {data?.has_pulse && data.pulse_recent && (
            <ExpandableSection
              icon={Wind}
              title="Tendance récente Pulse"
              badge={fmtDate(data.pulse_recent.created_at)}
              testid="section-pulse"
            >
              <div className="flex items-baseline gap-3">
                <div className="text-3xl tabular-nums" style={{ color: T.charcoal,
                     fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>
                  {Math.round(data.pulse_recent.score * 10) / 10}
                </div>
                <div className="text-[11px] uppercase tracking-[0.15em]" style={{ color: T.muted }}>
                  {data.pulse_recent.domain || 'score Pulse'}
                </div>
              </div>
            </ExpandableSection>
          )}
        </div>
      )}
    </div>
  );
}
